import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import Textbox from 'carbon-react/lib/components/textbox';

import { LiveReportList } from '@futrli-sdk/predict';
import { getReportingExportedPdfsUrl } from '@futrli/shared-services';
import { LoadingWizardScreen } from '@futrli/ui-components/components/sidebar/wizard/screens/loading-wizard-screen/loading-wizard-screen';
import {
  FooterMode,
  WizardScreen
} from '@futrli/ui-components/components/sidebar/wizard/screens/wizard-screen/wizard-screen';
import { WizardFinished } from '@futrli/ui-components/components/sidebar/wizard/wizard-finished/wizard-finished';
import { WizardFormError } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-error/wizard-form-error';
import { WizardFormValidationErrorSummary } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-validation-error-summary/wizard-form-validation-error-summary';
import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';

import { useApiView } from '@futrli/views/hooks/useApiView';
import { ReportDocumentView } from '@futrli/views/views/reports/ReportDocumentView/ReportDocumentView';

import { useExportPolling } from './hooks/use-export-polling';
import { useRouteIds } from '../../../hooks/useRouteIds';

export interface ExportReportWizardContainerProps {
  onClose: () => void;
  report: LiveReportList;
}

type Screen = 'form' | 'exporting' | 'exported';

const FIELD_ID = 'export-pdf-name';

export const ExportReportWizardContainer = ({ onClose, report }: ExportReportWizardContainerProps) => {
  const navigate = useNavigate();
  const intl = useIntl();

  const { workspaceId, organisationId, isConsolidation } = useRouteIds();

  const view = useApiView(
    useMemo(() => new ReportDocumentView(workspaceId, organisationId, isConsolidation), [
      workspaceId,
      organisationId,
      isConsolidation
    ])
  );

  const [screen, setScreen] = useState<Screen>('form');
  const [pdfName, setPdfName] = useState(report.name);
  const [error, setError] = useState<string | undefined>();
  const [validationErrors, setValidationErrors] = useState<Record<string, { id: string; message: string }>>({});
  const hasBeenValidated = useRef(false);

  const validate = useCallback(() => {
    const errors: Record<string, { id: string; message: string }> = {};

    if (!pdfName.trim()) {
      errors[FIELD_ID] = {
        id: FIELD_ID,
        message: intl.formatMessage({
          id: 'reporting.validation.pdf_name_is_required',
          defaultMessage: 'PDF name is required'
        })
      };
    }

    setValidationErrors(errors);
    hasBeenValidated.current = true;
    return Object.keys(errors).length === 0;
  }, [pdfName, intl]);

  const revalidate = useCallback(() => {
    if (hasBeenValidated.current) {
      validate();
    }
  }, [validate]);

  // Poll for export completion
  const { isCompleted: exportCompleted, errorMessage: pollingError, reset: resetPolling } = useExportPolling({
    exportedReportId: view.state.exportedReportId,
    workspaceId,
    organisationId,
    isConsolidation,
    enabled: screen === 'exporting'
  });

  useEffect(() => {
    if (exportCompleted) {
      setScreen('exported');
    }
  }, [exportCompleted]);

  useEffect(() => {
    if (pollingError) {
      setError(pollingError);
      resetPolling();
      setScreen('form');
    }
  }, [pollingError, resetPolling]);

  // Transition to exporting when mutation is pending
  useEffect(() => {
    if (view.state.isExporting) {
      setScreen('exporting');
    }
  }, [view.state.isExporting]);

  // Handle export error
  useEffect(() => {
    if (view.state.errorExporting) {
      setError(intl.formatMessage({ id: 'reporting.try_again', defaultMessage: 'Try again.' }));
      setScreen('form');
    }
  }, [view.state.errorExporting, intl]);

  const handleExport = useCallback(() => {
    if (!validate()) {
      return;
    }

    setError(undefined);
    view.exportReportToPdf({
      data: {
        report_id: report.id,
        name: pdfName.trim()
      }
    });
  }, [validate, view, report.id, pdfName]);

  const handleGoToExports = useCallback(() => {
    onClose();
    navigate(getReportingExportedPdfsUrl(workspaceId, organisationId, isConsolidation));
  }, [navigate, workspaceId, organisationId, isConsolidation, onClose]);

  if (screen === 'exporting') {
    return (
      <LoadingWizardScreen text={intl.formatMessage({ id: 'reporting.exporting', defaultMessage: 'Exporting...' })} />
    );
  }

  if (screen === 'exported') {
    return (
      <WizardScreen
        footerMode={FooterMode.CREATED}
        onCancel={onClose}
        onView={handleGoToExports}
        viewText={intl.formatMessage({ id: 'reporting.go_to_exports', defaultMessage: 'Go to exports' })}
      >
        <WizardFinished
          text={intl.formatMessage({
            id: 'reporting.exported_to_pdf',
            defaultMessage: 'Exported to PDF'
          })}
        />
      </WizardScreen>
    );
  }

  return (
    <WizardScreen
      footerMode={FooterMode.CREATE}
      onCancel={onClose}
      onCreate={handleExport}
      createText={intl.formatMessage({ id: 'common.save', defaultMessage: 'Save' })}
    >
      <>
        {error && <WizardFormError message={error} mb={3} />}

        <WizardFormValidationErrorSummary errors={validationErrors} mb={3} />

        <WizardRequiredFieldInfo />

        <Box mb={2}>
          <Textbox
            label={intl.formatMessage({ id: 'reporting.report', defaultMessage: 'Report' })}
            value={report.name}
            onChange={() => undefined}
            disabled
            required
            mb={0}
          />
        </Box>

        <Box>
          <Textbox
            label={intl.formatMessage({ id: 'reporting.pdf_name', defaultMessage: 'PDF name' })}
            name={FIELD_ID}
            id={FIELD_ID}
            value={pdfName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPdfName(e.target.value)}
            onBlur={revalidate}
            error={validationErrors[FIELD_ID]?.message}
            required
          />
        </Box>
      </>
    </WizardScreen>
  );
};
